import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Sidebar.css';
import { FaHome, FaCaretDown, FaFileContract, FaAddressBook, FaMoneyBillWave, FaChevronLeft, FaChevronRight, FaCog } from 'react-icons/fa';

const Sidebar = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarRef = useRef(null);

  const menuGroups = [
    {
      key: 'dashboard',
      type: 'link',
      label: '儀表板',
      icon: <FaHome />,
      path: '/',
    },
    {
      key: 'contracts-customers',
      type: 'group',
      label: '合約與客戶',
      icon: <FaFileContract />,
      items: [
        { label: '合約管理', path: '/contracts' },
        { label: '客戶管理', path: '/customers' },
      ],
    },
    {
      key: 'business',
      type: 'group',
      label: '業務中心',
      icon: <FaAddressBook />,
      items: [
        { label: '報價單管理', path: '/quotations' },
        { label: '產品管理', path: '/assets' },
      ],
    },
    {
      key: 'finance',
      type: 'group',
      label: '財務中心',
      icon: <FaMoneyBillWave />,
      items: [
        { label: '請款單列表', path: '/invoices' },
        { label: '帳務管理', path: '/dashboard' },
      ],
    },
    {
      key: 'settings',
      type: 'group',
      label: '系統設定',
      icon: <FaCog />,
      items: [
        ...(['admin', 'superadmin'].includes(currentUser?.role)
          ? [{ label: '公司基本資料', path: '/company-settings' }]
          : []),
        // CARQ-95：合約範本不分權限（Bridget 確認系統只有一位使用者）
        { label: '合約範本', path: '/contract-templates' },
      ],
    },
  ];

  // Auto-expand group containing current path
  useEffect(() => {
    const newOpenGroups = {};
    menuGroups.forEach(group => {
      if (group.type === 'group') {
        const isActive = group.items.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
        if (isActive) {
          newOpenGroups[group.key] = true;
        }
      }
    });
    setOpenGroups(prev => ({ ...prev, ...newOpenGroups }));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setMobileOpen(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const toggleGroup = (key) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenGroups(prev => ({ ...prev, [key]: true }));
      return;
    }
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const closeMobile = () => setMobileOpen(false);

  if (!currentUser) return null;

  return (
    <>
      {/* Mobile hamburger trigger */}
      <button className="sidebar-mobile-trigger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="選單">
        ☰
      </button>

      {/* Mobile overlay */}
      {mobileOpen && <div className="sidebar-overlay" onClick={closeMobile} />}

      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`} ref={sidebarRef}>
        <div className="sidebar__collapse-btn" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </div>

        <nav className="sidebar__nav">
          {menuGroups.map(group => {
            if (group.type === 'link') {
              return (
                <div key={group.key} className="sidebar__item">
                  <Link
                    to={group.path}
                    className={`sidebar__link ${isActive(group.path) ? 'sidebar__link--active' : ''}`}
                    onClick={closeMobile}
                    title={collapsed ? group.label : undefined}
                  >
                    <span className="sidebar__icon">{group.icon}</span>
                    {!collapsed && <span className="sidebar__label">{group.label}</span>}
                  </Link>
                </div>
              );
            }

            const groupActive = group.items.some(item => isActive(item.path));
            const isOpen = openGroups[group.key];

            return (
              <div key={group.key} className="sidebar__group">
                <div
                  className={`sidebar__group-header ${groupActive ? 'sidebar__group-header--active' : ''}`}
                  onClick={() => toggleGroup(group.key)}
                  title={collapsed ? group.label : undefined}
                >
                  <span className="sidebar__icon">{group.icon}</span>
                  {!collapsed && (
                    <>
                      <span className="sidebar__label">{group.label}</span>
                      <FaCaretDown className={`sidebar__caret ${isOpen ? 'sidebar__caret--open' : ''}`} />
                    </>
                  )}
                </div>
                {!collapsed && isOpen && (
                  <div className="sidebar__submenu">
                    {group.items.map(item => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar__sublink ${isActive(item.path) ? 'sidebar__sublink--active' : ''}`}
                        onClick={closeMobile}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
